/**
 * Loan Management Page
 * Track company loans with banks
 */
import { useState, useEffect } from 'react';
import { 
  Landmark, Plus, Edit2, Trash2, DollarSign, Calendar, Percent,
  TrendingDown, CheckCircle, AlertTriangle, CreditCard, Building2,
  ChevronDown, ChevronUp, X, Save, RefreshCw, Clock, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOAN_TYPES = [
  'Term Loan',
  'Overdraft',
  'Trade Financing',
  'Working Capital',
  'Equipment Financing',
  'Property Loan',
  'SME Loan',
  'Other'
];

const BANKS = [
  'Maybank',
  'CIMB Bank',
  'Public Bank',
  'RHB Bank',
  'Hong Leong Bank',
  'AmBank',
  'Bank Islam',
  'OCBC Bank',
  'UOB Bank',
  'Standard Chartered',
  'HSBC',
  'Other'
];

const LoanPage = () => {
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const [loanPayments, setLoanPayments] = useState({});

  const [formData, setFormData] = useState({
    bank_name: 'Maybank',
    loan_type: 'Term Loan',
    principal_amount: '',
    interest_rate: '',
    tenure_months: '',
    start_date: new Date().toISOString().split('T')[0],
    monthly_payment: '',
    purpose: '',
    collateral: '',
    status: 'Active'
  });

  const [paymentData, setPaymentData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    principal_portion: '',
    interest_portion: '',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [loansRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/loans`),
        fetch(`${API_URL}/api/loans/summary`)
      ]);
      
      const loansData = await loansRes.json();
      const summaryData = await summaryRes.json();
      
      setLoans(loansData);
      setSummary(summaryData);
    } catch (err) {
      console.error('Error fetching loans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoanPayments = async (loanId) => {
    try {
      const res = await fetch(`${API_URL}/api/loans/payments/${loanId}`);
      const data = await res.json();
      setLoanPayments(prev => ({ ...prev, [loanId]: data }));
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  };

  const handleSaveLoan = async () => {
    try {
      const url = editingLoan 
        ? `${API_URL}/api/loans/${editingLoan.id}`
        : `${API_URL}/api/loans`;
      
      const res = await fetch(url, {
        method: editingLoan ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          principal_amount: parseFloat(formData.principal_amount) || 0,
          interest_rate: parseFloat(formData.interest_rate) || 0,
          tenure_months: parseInt(formData.tenure_months) || 0,
          monthly_payment: parseFloat(formData.monthly_payment) || 0
        })
      });

      if (res.ok) {
        setShowAddModal(false);
        setEditingLoan(null);
        resetForm();
        fetchData();
      }
    } catch (err) {
      console.error('Error saving loan:', err);
    }
  };

  const handleDeleteLoan = async (id) => {
    if (!window.confirm('Padam pinjaman ini? Semua rekod bayaran juga akan dipadam.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loans/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error deleting loan:', err);
    }
  };

  const handleAddPayment = async () => {
    if (!selectedLoan) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loans/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loan_id: selectedLoan.id,
          ...paymentData,
          amount: parseFloat(paymentData.amount) || 0,
          principal_portion: parseFloat(paymentData.principal_portion) || 0,
          interest_portion: parseFloat(paymentData.interest_portion) || 0
        })
      });

      if (res.ok) {
        setShowPaymentModal(false);
        setSelectedLoan(null);
        resetPaymentForm();
        fetchData();
        if (expandedLoan) {
          fetchLoanPayments(expandedLoan);
        }
      }
    } catch (err) {
      console.error('Error adding payment:', err);
    }
  };

  const handleDeletePayment = async (paymentId, loanId) => {
    if (!window.confirm('Padam bayaran ini?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loans/payments/${paymentId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchData();
        fetchLoanPayments(loanId);
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
    }
  };

  const openEditModal = (loan) => {
    setFormData({
      bank_name: loan.bank_name,
      loan_type: loan.loan_type,
      principal_amount: loan.principal_amount.toString(),
      interest_rate: loan.interest_rate.toString(),
      tenure_months: loan.tenure_months.toString(),
      start_date: loan.start_date,
      monthly_payment: loan.monthly_payment.toString(),
      purpose: loan.purpose || '',
      collateral: loan.collateral || '',
      status: loan.status
    });
    setEditingLoan(loan);
    setShowAddModal(true);
  };

  const openPaymentModal = (loan) => {
    setSelectedLoan(loan);
    setPaymentData({
      payment_date: new Date().toISOString().split('T')[0],
      amount: loan.monthly_payment.toString(),
      principal_portion: '',
      interest_portion: '',
      reference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const toggleExpand = (loanId) => {
    if (expandedLoan === loanId) {
      setExpandedLoan(null);
    } else {
      setExpandedLoan(loanId);
      if (!loanPayments[loanId]) {
        fetchLoanPayments(loanId);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      bank_name: 'Maybank',
      loan_type: 'Term Loan',
      principal_amount: '',
      interest_rate: '',
      tenure_months: '',
      start_date: new Date().toISOString().split('T')[0],
      monthly_payment: '',
      purpose: '',
      collateral: '',
      status: 'Active'
    });
  };

  const resetPaymentForm = () => {
    setPaymentData({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      principal_portion: '',
      interest_portion: '',
      reference: '',
      notes: ''
    });
  };

  const formatCurrency = (value) => {
    return `RM ${Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getProgressPercent = (loan) => {
    if (!loan.total_payable || loan.total_payable === 0) return 0;
    return Math.min(100, (loan.total_paid / loan.total_payable) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="loan-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-red-700 mx-auto mb-3" />
          <p className="text-gray-600">Loading loans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="loan-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-red-700" /> Loan Management
          </h1>
          <p className="text-gray-500 text-sm">Urus pinjaman syarikat dengan bank</p>
        </div>
        <button
          onClick={() => { resetForm(); setEditingLoan(null); setShowAddModal(true); }}
          className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2"
          data-testid="add-loan-btn"
        >
          <Plus className="w-4 h-4" /> Tambah Pinjaman
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="text-gray-500 text-sm">Jumlah Pinjaman</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{summary.active_loans || 0}</p>
          <p className="text-xs text-gray-500">Aktif dari {summary.total_loans || 0} total</p>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-red-600" />
            <span className="text-gray-500 text-sm">Baki Tertunggak</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_outstanding)}</p>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-amber-600" />
            <span className="text-gray-500 text-sm">Bayaran Bulanan</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary.total_monthly_payment)}</p>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="text-gray-500 text-sm">Jumlah Dibayar</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_paid)}</p>
        </div>
      </div>

      {/* Loans List */}
      <div className="space-y-4">
        {loans.length > 0 ? loans.map((loan) => (
          <div key={loan.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Loan Header */}
            <div className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${loan.status === 'Paid Off' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <Building2 className={`w-6 h-6 ${loan.status === 'Paid Off' ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{loan.bank_name}</h3>
                    <p className="text-sm text-gray-500">{loan.loan_type}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Percent className="w-3 h-3" /> {loan.interest_rate}% p.a.
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {loan.tenure_months} bulan
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-xs text-gray-500">Baki Tertunggak</p>
                  <p className={`text-xl font-bold ${loan.status === 'Paid Off' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(loan.outstanding_balance)}
                  </p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                    loan.status === 'Active' ? 'bg-blue-100 text-blue-700' :
                    loan.status === 'Paid Off' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {loan.status}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Dibayar: {formatCurrency(loan.total_paid)}</span>
                  <span>Jumlah: {formatCurrency(loan.total_payable)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${loan.status === 'Paid Off' ? 'bg-emerald-500' : 'bg-red-600'}`}
                    style={{ width: `${getProgressPercent(loan)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {getProgressPercent(loan).toFixed(1)}% selesai • {loan.payments_made || 0} bayaran
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => toggleExpand(loan.id)}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  {expandedLoan === loan.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {expandedLoan === loan.id ? 'Tutup' : 'Lihat Bayaran'}
                </button>
                
                <div className="flex items-center gap-2">
                  {loan.status === 'Active' && (
                    <button
                      onClick={() => openPaymentModal(loan)}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Bayar
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(loan)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteLoan(loan.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Payment History (Expandable) */}
            {expandedLoan === loan.id && (
              <div className="border-t border-gray-100 bg-gray-50 p-4">
                <h4 className="font-medium text-gray-700 mb-3">Sejarah Bayaran</h4>
                {loanPayments[loan.id]?.length > 0 ? (
                  <div className="space-y-2">
                    {loanPayments[loan.id].map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{payment.payment_date}</p>
                          <p className="text-xs text-gray-500">
                            Principal: {formatCurrency(payment.principal_portion)} | Interest: {formatCurrency(payment.interest_portion)}
                          </p>
                          {payment.reference && <p className="text-xs text-gray-400">Ref: {payment.reference}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-bold text-emerald-600">{formatCurrency(payment.amount)}</p>
                          <button
                            onClick={() => handleDeletePayment(payment.id, loan.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">Tiada rekod bayaran</p>
                )}
              </div>
            )}
          </div>
        )) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Landmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Tiada pinjaman direkodkan</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-sm"
            >
              Tambah Pinjaman Pertama
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Loan Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" data-testid="loan-modal">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold text-gray-800">
                {editingLoan ? 'Edit Pinjaman' : 'Tambah Pinjaman Baru'}
              </h3>
              <button onClick={() => { setShowAddModal(false); setEditingLoan(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                  <select
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  >
                    {BANKS.map(bank => <option key={bank} value={bank}>{bank}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Pinjaman</label>
                  <select
                    value={formData.loan_type}
                    onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  >
                    {LOAN_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Pinjaman (RM)</label>
                  <input
                    type="number"
                    value={formData.principal_amount}
                    onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="100000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kadar Faedah (% p.a.)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="5.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tempoh (Bulan)</label>
                  <input
                    type="number"
                    value={formData.tenure_months}
                    onChange={(e) => setFormData({ ...formData, tenure_months: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="60"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bayaran Bulanan (RM)</label>
                  <input
                    type="number"
                    value={formData.monthly_payment}
                    onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="2000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh Mula</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tujuan Pinjaman</label>
                <input
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="Working capital, equipment purchase, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cagaran (jika ada)</label>
                <input
                  type="text"
                  value={formData.collateral}
                  onChange={(e) => setFormData({ ...formData, collateral: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="Property, fixed deposit, etc."
                />
              </div>

              {editingLoan && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  >
                    <option value="Active">Active</option>
                    <option value="Paid Off">Paid Off</option>
                    <option value="Restructured">Restructured</option>
                  </select>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => { setShowAddModal(false); setEditingLoan(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Batal
              </button>
              <button
                onClick={handleSaveLoan}
                disabled={!formData.principal_amount || !formData.monthly_payment}
                className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && selectedLoan && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl" data-testid="payment-modal">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">Tambah Bayaran</h3>
                <p className="text-sm text-gray-500">{selectedLoan.bank_name} - {selectedLoan.loan_type}</p>
              </div>
              <button onClick={() => { setShowPaymentModal(false); setSelectedLoan(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">Baki Tertunggak: <span className="font-bold text-red-600">{formatCurrency(selectedLoan.outstanding_balance)}</span></p>
                <p className="text-sm text-gray-600">Bayaran Bulanan: <span className="font-bold">{formatCurrency(selectedLoan.monthly_payment)}</span></p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh Bayaran</label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Bayaran (RM)</label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="0.00"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bahagian Principal (RM)</label>
                  <input
                    type="number"
                    value={paymentData.principal_portion}
                    onChange={(e) => setPaymentData({ ...paymentData, principal_portion: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bahagian Faedah (RM)</label>
                  <input
                    type="number"
                    value={paymentData.interest_portion}
                    onChange={(e) => setPaymentData({ ...paymentData, interest_portion: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. Rujukan</label>
                <input
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="TRX123456"
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowPaymentModal(false); setSelectedLoan(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Batal
              </button>
              <button
                onClick={handleAddPayment}
                disabled={!paymentData.amount}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Simpan Bayaran
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanPage;
